/**
 * Train Simulator config.
 *
 * Train Sim is a fixed-route puzzle:  5 trains start at authored spawn
 * markers and follow track tiles toward authored goal markers. The player
 * intervenes by tapping signal LAMPS (red/green) and track SWITCHES
 * (state A / state B) so every train reaches its goal without two trains
 * sharing a cell on the same frame.
 *
 * Grid is owned here — NOT borrowed from pipelineConfig — so a future
 * platformer schema can declare its own dimensions without touching shared
 * code.
 *
 * Connection model — owned locally (no longer re-exported from pipeline)
 * because trainsim supports diagonal track in addition to cardinal track.
 * Directions are indexed clockwise from North in 45° increments:
 *
 *     0 = N    1 = NE    2 = E    3 = SE
 *     4 = S    5 = SW    6 = W    7 = NW
 *
 * `OPPOSITE[d] = (d + 4) & 7`, so the model is symmetric. Tile rotation
 * in saved stage data still means "quarter turns clockwise" (rot ∈ 0..3),
 * which corresponds to a 2-index shift of the 8-element connection array.
 */

export const TRAINSIM_CONFIG = {
  // Base train speed in cells/sec (multiplied by the player's tempo
  // selector: 1×/2×/4×). The 1.20 value previously chosen for "brisk"
  // play turned out to be too fast for players to read the board and
  // re-plan switches in real time, so we've dialled it back to ~55 %
  // of that. Players who want a quicker pace still have 2× / 4× in
  // the HUD speed selector.
  TRAIN_BASE_SPEED: 0.65,

  // ── Dynamic cruise / brake model ───────────────────────────
  // Trains no longer roll at a single flat speed: they CRUISE at
  // `TRAIN_CRUISE_FACTOR` × the base speed on open track and ease
  // down toward `TRAIN_SLOW_FACTOR` only when the switch lamp
  // ahead is RED (so a red signal is telegraphed by the train
  // visibly braking before it freezes at the switch). On green the
  // train accelerates back up to cruise. All of this multiplies on
  // top of the manual 1×/2×/4× TEMPÓ the player picks in the HUD.
  //
  //   TRAIN_CRUISE_FACTOR     - open-track multiplier (≈ "2× today")
  //   TRAIN_SLOW_FACTOR       - floor approached when braking for red
  //   TRAIN_BRAKE_RADIUS_CELLS- how many cells out the brake begins
  //   TRAIN_BRAKE_EASE_RATE   - factor units/sec the speed eases at
  TRAIN_CRUISE_FACTOR: 2.0,
  TRAIN_SLOW_FACTOR: 1.0,
  TRAIN_BRAKE_RADIUS_CELLS: 2,
  TRAIN_BRAKE_EASE_RATE: 3.0,

  // ── Automatic level-crossing barrier (sorompó) ─────────────
  // Barriers are car-only and fully automatic now: they drop when a
  // train is within `BARRIER_CLOSE_RADIUS_CELLS` of the crossing
  // track cell and stay down for `BARRIER_LINGER_SEC` after the
  // train has passed (so the arm doesn't chatter up/down on a train
  // that straddles the radius edge). They NEVER stop a train.
  BARRIER_CLOSE_RADIUS_CELLS: 2,
  BARRIER_LINGER_SEC: 0.6,

  // ── Distant train-horn warning ─────────────────────────────
  // A one-shot horn sounds when a train first comes within
  // `TRAIN_HORN_RADIUS_CELLS` cells of ANY road cell (i.e. it's
  // approaching a level crossing). It re-arms only after the train
  // pulls back past `TRAIN_HORN_RADIUS_CELLS + 1` so a train idling
  // near a road doesn't blast the horn every frame.
  TRAIN_HORN_RADIUS_CELLS: 3,

  // 5 train colours keyed by trainId. Used as sprite tint when no
  // PNG skin is set, and as the goal / spawn marker colour so the
  // player can see which marker goes with which train.
  TRAIN_COLORS: {
    t1: 0xef4444, // red
    t2: 0x3b82f6, // blue
    t3: 0xfbbf24, // yellow
    t4: 0x22c55e, // green
    t5: 0xa855f7, // purple
  },

  TRAIN_IDS: ["t1", "t2", "t3", "t4", "t5"],

  // Visual palette for the procedural fallbacks (lamps, switches,
  // spawn/goal rings). Mirrors pipeline's palette idiom.
  COLOR_GRID_BG: 0x1e293b,
  COLOR_GRID_LINE: 0x334155,
  COLOR_TILE_BG: 0x44403c,
  COLOR_LAMP_RED: 0xef4444,
  COLOR_LAMP_GREEN: 0x22c55e,
  COLOR_SWITCH_A: 0xfbbf24, // amber (ghost / next-state preview)
  COLOR_SWITCH_B: 0x60a5fa, // light blue (theme token for editor swatches)
  // Hot magenta used specifically for the ACTIVE routed pair on a
  // switch. Picked to be the most attention-grabbing colour in the
  // palette: no collision with the amber ghost, green/red lamps, or
  // the cool-blue game-area chrome. Kept as a separate constant so
  // the COLOR_SWITCH_B theme token (used by editor palette swatches
  // and exported in data/trainsim.config.js) stays untouched.
  COLOR_SWITCH_ACTIVE: 0xff2dd6,
  COLOR_STATION: 0xa855f7,

  POINTS_PER_TRAIN_GOAL: 250,
};

/**
 * Train Sim's grid. Fixed-size like Pipeline's, but DECLARED HERE so a
 * future platformer schema can use different dimensions without touching
 * shared code.
 *
 * Dimensions cover the full 1920×1080 canvas at 30 px per cell
 * (64 × 30 = 1920, 25 × 30 = 750). The grid is 25 rows tall — the
 * top 8 and bottom 3 rows of the previous 36-row layout were trimmed
 * to give the HUD bands (PONT/ÉLET/MENETREND/IDŐ at the top, switch
 * tiles + legend at the bottom) clean room outside the play area.
 * (Originally the bottom trim was 5 rows; 2 rows were given back so
 * stages have a touch more vertical breathing room above the bottom
 * HUD tiles.) The 750-px-tall board is vertically centred inside the
 * 1080-px canvas, leaving ~165 px of HUD space at top and bottom.
 */
export const TRAINSIM_GRID = Object.freeze({
  cols: 64,
  rows: 25,
  cellSize: 30,
  originX: 0,
  originY: 0,
});

/**
 * Per-tile connection definitions, indexed [N, NE, E, SE, S, SW, W, NW].
 *
 * Cardinal-only tiles keep their diagonal slots false. The `diagonal`
 * tile is the only one that uses the corner indices today — it's a 45°
 * straight that links the NE corner of cell (c, r) to the SW corner,
 * forming a chain of diagonal track when stacked NE-by-NE.
 *
 * Rotation is applied via `rotateConnections(conn, rot)`, where `rot`
 * is the quarter-turn count (0..3 == 0/90/180/270° clockwise). That
 * shifts the array by `rot * 2` indices, so cardinals stay cardinal
 * and diagonals stay diagonal after any rotation.
 */
export const TILE_DEFS = {
  straight: [true, false, false, false, true, false, false, false], // N-S
  curve: [true, false, true, false, false, false, false, false], // N-E (90° turn)
  t: [true, false, true, false, true, false, false, false], // N-E-S (trunk W closed)
  cross: [true, false, true, false, true, false, true, false], // all 4 cardinals
  deadend: [true, false, false, false, false, false, false, false], // only N
  diagonal: [false, true, false, false, false, true, false, false], // NE-SW 45°
  // 45° bend tiles — half-cardinal + half-diagonal meeting at the
  // cell centre, so a horizontal/vertical line can transition into
  // a diagonal stroke without the abrupt 90° corner that `curve`
  // would draw. Two mirror-image base tiles cover all 8 cardinal +
  // far-corner pairs once rotated through their four quarter-turns:
  //
  //   bendr  base = N + SE  (clockwise / right turn out of N)
  //          rot 0 N→SE,  rot 1 E→SW,  rot 2 S→NW,  rot 3 W→NE
  //   bendl  base = N + SW  (counter-clockwise / left turn out of N)
  //          rot 0 N→SW,  rot 1 E→NW,  rot 2 S→NE,  rot 3 W→SE
  //
  // The "far corner" choice (opposite half of the cell from the
  // cardinal port) is what gives the bend its 45° geometric angle —
  // the half-diagonal from the cell centre to the corner runs at
  // exactly 45° from the half-cardinal segment.
  bendr: [true, false, false, true, false, false, false, false], // N + SE
  bendl: [true, false, false, false, false, true, false, false], // N + SW
  // Y-junction: full NE-SW diagonal pass-through with ONE cardinal
  // stub branching off, so a horizontal/vertical straight run can
  // T into the middle of a diagonal run. Two base tiles cover all
  // eight orientations once rotated:
  //
  //   diagy   base = NE + SW + W  (NE-SW diagonal, cardinal W stub)
  //           rot 0 NE-SW+W, rot 1 NW-SE+N, rot 2 NE-SW+E, rot 3 NW-SE+S
  //   diagy2  base = NE + SW + N  (NE-SW diagonal, cardinal N stub)
  //           rot 0 NE-SW+N, rot 1 NW-SE+E, rot 2 NE-SW+S, rot 3 NW-SE+W
  diagy: [false, true, false, false, false, true, true, false], // NE + SW + W
  diagy2: [true, true, false, false, false, true, false, false], // NE + SW + N
  // Mirror Y: full CARDINAL pass-through with one DIAGONAL stub,
  // so a horizontal/vertical straight run can branch off into a
  // diagonal at any point along it. Two base tiles cover all eight
  // orientations:
  //
  //   wyec   base = W + E + NE  (horizontal pass, NE branch)
  //          rot 0 W-E+NE, rot 1 N-S+SE, rot 2 W-E+SW, rot 3 N-S+NW
  //   wyec2  base = W + E + NW  (horizontal pass, NW branch)
  //          rot 0 W-E+NW, rot 1 N-S+NE, rot 2 W-E+SE, rot 3 N-S+SW
  wyec: [false, true, true, false, false, false, true, false], // W + E + NE
  wyec2: [false, false, true, false, false, false, true, true], // W + E + NW
};

/**
 * Road tile connection table — cardinal-only counterpart of TILE_DEFS
 * used by the road layer. Same 8-slot encoding (N=0, NE=1, E=2, …)
 * so all the existing helpers (rotateConnections, getExitDir,
 * DIR_DELTA, OPPOSITE) work unchanged for cars. We deliberately
 * keep diagonals OFF the road palette because asphalt visuals don't
 * read well at 45°; level-crossings and curves stay cardinal.
 *
 * The five shapes cover every realistic road layout:
 *   straight  - through-street
 *   curve     - 90° bend
 *   t         - T-junction (two through + one branch)
 *   cross     - 4-way intersection
 *   deadend   - cul-de-sac
 *
 * Type-string collisions with track TILE_DEFS are by design — a tile
 * on the 'road' layer with type 'straight' is a road, and the same
 * type on the 'track' layer is a rail. `TrainSimBoard._paintTile`
 * branches on `layer.id === 'road'` to pick the right drawer + grid.
 */
export const ROAD_TILE_DEFS = {
  straight: [true, false, false, false, true, false, false, false], // N-S
  curve: [true, false, true, false, false, false, false, false], // N-E (90°)
  t: [true, false, true, false, true, false, false, false], // N-E-S
  cross: [true, false, true, false, true, false, true, false], // 4-way
  deadend: [true, false, false, false, false, false, false, false], // N-only
};

/**
 * Per-direction neighbour delta. Indices match the connection array
 * layout above. Diagonal exits move into a cell offset by 1 col AND
 * 1 row simultaneously — the train's spline path covers √2 × cellSize,
 * which `TrainSimTrain` compensates for so on-screen speed stays
 * constant whether the segment is cardinal or diagonal.
 */
export const DIR_DELTA = [
  { dc: 0, dr: -1 }, // 0 N
  { dc: 1, dr: -1 }, // 1 NE
  { dc: 1, dr: 0 }, // 2 E
  { dc: 1, dr: 1 }, // 3 SE
  { dc: 0, dr: 1 }, // 4 S
  { dc: -1, dr: 1 }, // 5 SW
  { dc: -1, dr: 0 }, // 6 W
  { dc: -1, dr: -1 }, // 7 NW
];

/** `OPPOSITE[d]` = the direction 180° away. */
export const OPPOSITE = [4, 5, 6, 7, 0, 1, 2, 3];

/**
 * Catalog of CITY names available for spawn/goal banners.
 *
 * Each train id (t1..t5) is hard-linked to a station PNG in
 * `data/trainsim.config.js` whose name is baked into the artwork
 * (BUDAPEST-KELETI, BUDAPEST-NYUGATI, …). This catalog lets the
 * stage designer pick a DESTINATION CITY for each goal so the
 * player sees "BUDAPEST-KELETI → SZOLNOK" instead of just a
 * decontextualised T1 stop sign.
 *
 * `value` is what gets stored on the entity (and rendered on the
 * banner). `label` is the editor-friendly form (titlecase). Add
 * new destinations here and they'll automatically appear in the
 * inspector dropdown next to every goal.
 */
export const TRAINSIM_CITIES = Object.freeze([
  // Default / "use the train id's built-in origin name"
  { value: "", label: "(Default — origin city)" },

  // Hungarian stations matching the spawn building PNGs
  { value: "BUDAPEST-KELETI", label: "Budapest-Keleti" },
  { value: "BUDAPEST-NYUGATI", label: "Budapest-Nyugati" },
  { value: "BUDAPEST-DÉLI", label: "Budapest-Déli" },
  { value: "SZOMBATHELY", label: "Szombathely" },
  { value: "SZOLNOK", label: "Szolnok" },
  { value: "MISKOLC-TISZAI", label: "Miskolc-Tiszai" },

  // Other large Hungarian cities a train could be headed to
  { value: "DEBRECEN", label: "Debrecen" },
  { value: "SZEGED", label: "Szeged" },
  { value: "PÉCS", label: "Pécs" },
  { value: "GYŐR", label: "Győr" },
  { value: "SZÉKESFEHÉRVÁR", label: "Székesfehérvár" },
  { value: "KECSKEMÉT", label: "Kecskemét" },
  { value: "NYÍREGYHÁZA", label: "Nyíregyháza" },
  { value: "KAPOSVÁR", label: "Kaposvár" },
  { value: "VESZPRÉM", label: "Veszprém" },
  { value: "EGER", label: "Eger" },
  { value: "SOPRON", label: "Sopron" },
  { value: "TATABÁNYA", label: "Tatabánya" },
  { value: "ZALAEGERSZEG", label: "Zalaegerszeg" },
  { value: "BÉKÉSCSABA", label: "Békéscsaba" },

  // Cross-border destinations frequently served from Hungary
  { value: "ORADEA", label: "Oradea (RO)" },
  { value: "ARAD", label: "Arad (RO)" },
  { value: "CLUJ-NAPOCA", label: "Cluj-Napoca (RO)" },
  { value: "TIMIȘOARA", label: "Timișoara (RO)" },
  { value: "BRATISLAVA", label: "Bratislava (SK)" },
  { value: "KOŠICE", label: "Košice (SK)" },
  { value: "WIEN", label: "Wien (AT)" },
  { value: "BEOGRAD", label: "Beograd (RS)" },
  { value: "ZAGREB", label: "Zagreb (HR)" },
  { value: "KRAKÓW", label: "Kraków (PL)" },
]);

/**
 * Rotate an 8-element connection array clockwise by `rot` quarter-turns.
 * (rot 0 = identity, rot 1 = 90°, etc.) Internally that's a 2-index
 * right-shift per quarter-turn, since 4 quarters span the full 8-slot
 * cycle.
 */
export function rotateConnections(conn, rot) {
  const n = conn.length;
  const shift = (((rot * 2) % n) + n) % n;
  const out = new Array(n);
  for (let i = 0; i < n; i++) out[(i + shift) % n] = conn[i];
  return out;
}

/**
 * Given a tile's live (rotated) connections and the index of the
 * direction the train ENTERED from, return the direction it should
 * EXIT through, or -1 if no valid exit is open.
 *
 * For tiles with exactly two open ports (straight, curve, diagonal,
 * bend, dead-end, an active switch state) there's only one candidate
 * so the result is deterministic. For junctions with three or more
 * open ports (T, cross, Y-junction family — wyec, wyec2, diagy,
 * diagy2) we used to pick the lowest-indexed slot, which had no
 * relationship to which way the train was actually going. That gave
 * the infamous "Y picks the sharp turn over the obvious straight
 * through" bug.
 *
 * Heading-aware fix: the train is moving in direction `OPPOSITE[entryDir]`
 * when it lands on the new cell. We pick the open exit with the
 * SMALLEST angular deviation from that heading — so passing through
 * a Y always continues straight by default and the sharp branch is
 * only taken when the train enters FROM the branch side (where the
 * straight isn't an option anyway). Routing through a junction with
 * more than one equally-natural exit can still be controlled
 * deterministically by overlaying a Track Switch entity.
 */
export function getExitDir(conn, entryDir) {
  if (entryDir < 0 || entryDir >= conn.length) {
    // Falling back to a fixed scan when we don't know where the
    // train came from (e.g. very first segment of a brand-new
    // spawn before the engine sets `_entryDir`). Keeps the old
    // "first open exit wins" semantics for that one degenerate
    // case so nothing regresses.
    for (let i = 0; i < conn.length; i++) {
      if (conn[i]) return i;
    }
    return -1;
  }
  const heading = OPPOSITE[entryDir];
  let bestDir = -1;
  let bestDelta = Infinity;
  for (let i = 0; i < conn.length; i++) {
    if (i === entryDir) continue;
    if (!conn[i]) continue;
    // Wrap-around angular distance on the 8-slot compass: a value
    // of 0 means "straight through", 1 means "45° turn", 4 means
    // "U-turn" (impossible here because entryDir is excluded).
    let d = Math.abs(i - heading);
    if (d > conn.length / 2) d = conn.length - d;
    if (d < bestDelta) {
      bestDelta = d;
      bestDir = i;
    }
  }
  return bestDir;
}

/** True if a direction index is one of the four diagonals (NE/SE/SW/NW). */
export function isDiagonalDir(dir) {
  return (dir & 1) === 1;
}

/**
 * Snake-style tram-track palette — matches `_renderTrail` in
 * `SnakeScene.js` so the trainsim board reads as the same world the
 * Tram game lives in. Three layers from bottom to top:
 *   - amber `BED` strip carrying the ties
 *   - brown `TIE` cross-pieces with darker `TIE_END` caps + cream `TIE_MID` middle
 *   - cream `RAIL` pair on top
 */
export const TRAINSIM_TRACK_PALETTE = Object.freeze({
  BED: 0x106eac,
  RAIL: 0xf6f6df,
  TIE_MID: 0xf4e3b2,
  TIE_END: 0xdeb969,
});

/**
 * Road palette — asphalt grey carriageway with cream edge stripes and
 * a dashed yellow centreline marking the two-direction divider. Read
 * by `drawTrainsimRoad` + `buildTrainsimRoadSvg` so palette thumbnails
 * and in-game tiles share the exact same colour vocabulary.
 *
 *   ASPHALT     - the road surface
 *   EDGE_LINE   - cream "fog line" along the outer edges of the road
 *   CENTRELINE  - dashed yellow line splitting the two lanes
 *   KERB        - thin darker strip just inside the cell border, so
 *                 adjacent road tiles still read as one continuous
 *                 surface (not as a kerb) — used only on cell sides
 *                 that DON'T have an open port (i.e. the road ends).
 */
export const TRAINSIM_ROAD_PALETTE = Object.freeze({
  ASPHALT: 0x3a3a40,
  EDGE_LINE: 0xe8e5d8,
  CENTRELINE: 0xf4c542,
  KERB: 0x2a2a30,
});

/**
 * Draw a tram-track tile in the snake-game style.
 *
 * Honours every connection set used by the trainsim grid (full NS / EW
 * straight, NE-corner family, T-junctions, dead-ends, cross, AND 45°
 * diagonals via the corner connection indices), picking the matching
 * half-cell strokes so corners and tees line up with the snake's visual
 * rhythm. Tie offsets are anchored to `cs/4` and `3*cs/4` for a uniform
 * pitch across cell boundaries.
 *
 * Drawn at (0,0) in the caller's container — pass a graphics object
 * already positioned at the desired tile origin.
 *
 * @param {import('../../../vendor/pixi.min.mjs').Graphics} g
 * @param {number} cs cell size in pixels
 * @param {boolean[]} conn 8-element [N, NE, E, SE, S, SW, W, NW]
 * @param {{ alpha?: number, bgColor?: number }} [options]
 */
export function drawTrainsimTrack(g, cs, conn, options = {}) {
  const { alpha = 1, bgColor = null } = options;

  if (bgColor != null) {
    g.roundRect(1, 1, cs - 2, cs - 2, 4);
    g.fill({ color: bgColor, alpha });
  }

  // Cardinal slots: N=0, E=2, S=4, W=6.
  // Diagonal slots: NE=1, SE=3, SW=5, NW=7.
  const n = conn[0],
    e = conn[2],
    s = conn[4],
    w = conn[6];
  const ne = conn[1],
    se = conn[3],
    sw = conn[5],
    nw = conn[7];
  if (!(n || e || s || w || ne || se || sw || nw)) return;

  const rg = Math.round(cs * 0.22);
  const rw = Math.max(2, Math.round(cs * 0.07));
  const tw = Math.max(3, Math.round(cs * 0.18));
  const ts = rg * 2 + rw * 6;
  const c = cs / 2;
  const bedW = rg * 2 + rw;
  const capLen = Math.max(1, Math.round(ts * 0.25));
  const TIE_OFFSETS = [cs / 4, (3 * cs) / 4];

  const drawTieH = (cx, cy) => {
    const left = cx - ts / 2;
    const top = cy - tw / 2;
    g.rect(left, top, capLen, tw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_END, alpha });
    g.rect(left + capLen, top, ts - capLen * 2, tw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_MID, alpha });
    g.rect(left + ts - capLen, top, capLen, tw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_END, alpha });
  };
  const drawTieV = (cx, cy) => {
    const left = cx - tw / 2;
    const top = cy - ts / 2;
    g.rect(left, top, tw, capLen);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_END, alpha });
    g.rect(left, top + capLen, tw, ts - capLen * 2);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_MID, alpha });
    g.rect(left, top + ts - capLen, tw, capLen);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_END, alpha });
  };

  const nsThru = n && s;
  const ewThru = e && w;

  // ── Curve tile (exactly two perpendicular cardinal ports, no
  // diagonals, no through-pair) ──────────────────────────────
  // Real tracks BEND at corners; the previous painter drew two
  // perpendicular half-strips and masked the seam with a big
  // circle blob. We now mirror the road painter's curve branch
  // and render the corner as a quarter-annulus: a curved bed,
  // two concentric rail arcs, and two perpendicular ties. The
  // arc is centred at the cell corner the rail bends AROUND so
  // the rail's centreline at the cell edges (radius = c) lines
  // up exactly with the straight tile's centreline — straights
  // and curves stitch together without seams.
  const cardinalCount = (n ? 1 : 0) + (e ? 1 : 0) + (s ? 1 : 0) + (w ? 1 : 0);
  const anyDiagonal = ne || se || sw || nw;
  const isCurve = cardinalCount === 2 && !nsThru && !ewThru && !anyDiagonal;
  if (isCurve) {
    let acx = 0,
      acy = 0,
      startAng = 0,
      endAng = 0;
    if (n && e) {
      acx = cs;
      acy = 0;
      startAng = Math.PI;
      endAng = Math.PI / 2;
    }
    if (e && s) {
      acx = cs;
      acy = cs;
      startAng = -Math.PI / 2;
      endAng = -Math.PI;
    }
    if (s && w) {
      acx = 0;
      acy = cs;
      startAng = 0;
      endAng = -Math.PI / 2;
    }
    if (w && n) {
      acx = 0;
      acy = 0;
      startAng = Math.PI / 2;
      endAng = 0;
    }

    const rOuter = c + bedW / 2;
    const rInner = c - bedW / 2;
    const rRailL = c - rg; // inner rail (closer to bend pivot)
    const rRailR = c + rg; // outer rail (farther from bend pivot)
    const rCentre = c;
    const SEGS = 24;
    const lerp = (t) => startAng + (endAng - startAng) * t;

    // BED — quarter annulus, outer forward then inner back so
    // the polygon stays simple-closed.
    const bedPts = [];
    for (let i = 0; i <= SEGS; i++) {
      const a = lerp(i / SEGS);
      bedPts.push(acx + Math.cos(a) * rOuter, acy + Math.sin(a) * rOuter);
    }
    for (let i = SEGS; i >= 0; i--) {
      const a = lerp(i / SEGS);
      bedPts.push(acx + Math.cos(a) * rInner, acy + Math.sin(a) * rInner);
    }
    g.poly(bedPts);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.BED, alpha });

    // RAILS — two thin arc-strips (one per rail), painted as a
    // ring of trapezoidal quads so the rails follow the curve
    // smoothly.
    const railStripe = (rMid) => {
      const r1 = rMid - rw / 2;
      const r2 = rMid + rw / 2;
      for (let i = 0; i < SEGS; i++) {
        const a1 = lerp(i / SEGS);
        const a2 = lerp((i + 1) / SEGS);
        g.poly([
          acx + Math.cos(a1) * r1,
          acy + Math.sin(a1) * r1,
          acx + Math.cos(a2) * r1,
          acy + Math.sin(a2) * r1,
          acx + Math.cos(a2) * r2,
          acy + Math.sin(a2) * r2,
          acx + Math.cos(a1) * r2,
          acy + Math.sin(a1) * r2,
        ]);
        g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
      }
    };
    railStripe(rRailL);
    railStripe(rRailR);

    // TIES — radial crossbars at 1/4 and 3/4 of the arc. Same
    // dark-cap / cream-middle look as the cardinal ties so a
    // straight ↔ curve transition reads as one continuous rail.
    const halfTs = ts / 2;
    const halfTw = tw / 2;
    for (const tFrac of [0.25, 0.75]) {
      const a = lerp(tFrac);
      const tcx = acx + Math.cos(a) * rCentre;
      const tcy = acy + Math.sin(a) * rCentre;
      // Tie axis runs RADIALLY (across the rails); the tie's
      // own thickness runs TANGENTIALLY (along the bed).
      const rx = Math.cos(a);
      const ry = Math.sin(a);
      const tx = -Math.sin(a);
      const ty = Math.cos(a);
      const aPt = { x: tcx + rx * halfTs, y: tcy + ry * halfTs };
      const bPt = { x: tcx - rx * halfTs, y: tcy - ry * halfTs };
      // Full tie (end-cap colour).
      g.poly([
        aPt.x + tx * halfTw,
        aPt.y + ty * halfTw,
        bPt.x + tx * halfTw,
        bPt.y + ty * halfTw,
        bPt.x - tx * halfTw,
        bPt.y - ty * halfTw,
        aPt.x - tx * halfTw,
        aPt.y - ty * halfTw,
      ]);
      g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_END, alpha });
      // Middle band (cream).
      const midLen = halfTs - capLen;
      const mA = { x: tcx + rx * midLen, y: tcy + ry * midLen };
      const mB = { x: tcx - rx * midLen, y: tcy - ry * midLen };
      g.poly([
        mA.x + tx * halfTw,
        mA.y + ty * halfTw,
        mB.x + tx * halfTw,
        mB.y + ty * halfTw,
        mB.x - tx * halfTw,
        mB.y - ty * halfTw,
        mA.x - tx * halfTw,
        mA.y - ty * halfTw,
      ]);
      g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_MID, alpha });
    }
    return;
  }

  if (nsThru) {
    g.rect(c - bedW / 2, 0, bedW, cs);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.BED, alpha });
    for (const off of TIE_OFFSETS) drawTieH(c, off);
    g.rect(c - rg - rw / 2, 0, rw, cs);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
    g.rect(c + rg - rw / 2, 0, rw, cs);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
  }

  if (ewThru) {
    g.rect(0, c - bedW / 2, cs, bedW);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.BED, alpha });
    for (const off of TIE_OFFSETS) drawTieV(off, c);
    g.rect(0, c - rg - rw / 2, cs, rw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
    g.rect(0, c + rg - rw / 2, cs, rw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
  }

  // Half-cell strokes — N owns the cs/4 tie, S owns 3cs/4, W owns cs/4,
  // E owns 3cs/4 so the uniform cs/2 pitch is preserved through corners,
  // tees and dead-ends.
  if (!nsThru && n) {
    g.rect(c - bedW / 2, 0, bedW, c);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.BED, alpha });
    drawTieH(c, TIE_OFFSETS[0]);
    g.rect(c - rg - rw / 2, 0, rw, c);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
    g.rect(c + rg - rw / 2, 0, rw, c);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
  }
  if (!nsThru && s) {
    g.rect(c - bedW / 2, c, bedW, c);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.BED, alpha });
    drawTieH(c, TIE_OFFSETS[1]);
    g.rect(c - rg - rw / 2, c, rw, c);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
    g.rect(c + rg - rw / 2, c, rw, c);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
  }
  if (!ewThru && e) {
    g.rect(c, c - bedW / 2, c, bedW);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.BED, alpha });
    drawTieV(TIE_OFFSETS[1], c);
    g.rect(c, c - rg - rw / 2, c, rw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
    g.rect(c, c + rg - rw / 2, c, rw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
  }
  if (!ewThru && w) {
    g.rect(0, c - bedW / 2, c, bedW);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.BED, alpha });
    drawTieV(TIE_OFFSETS[0], c);
    g.rect(0, c - rg - rw / 2, c, rw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
    g.rect(0, c + rg - rw / 2, c, rw);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
  }

  // Junction blob — masks the seam where two perpendicular half-cell
  // strokes meet (curves, T-junctions, dead-ends). Same trick the
  // snake's trail uses. Skipped when the tile has only diagonal
  // connections so the diagonal stroke doesn't get a stray dot.
  const anyCardinal = n || e || s || w;
  if (!nsThru && !ewThru && anyCardinal) {
    g.circle(c, c, rg + rw * 2);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_END, alpha });
  }

  // ── Diagonal track (45°) ────────────────────────────────────
  // Straight line corner-to-corner across the cell. Two distinct
  // orientations:
  //   NE-SW : conn[1] && conn[5]   (from top-right to bottom-left)
  //   NW-SE : conn[7] && conn[3]   (from top-left  to bottom-right)
  // Each is drawn as a wide bed strip with two parallel rails and
  // a pair of perpendicular ties — the same visual vocabulary as
  // the cardinal track, just rotated 45°.
  const drawDiagonal = (sx, sy, ex, ey) => {
    const dx = ex - sx,
      dy = ey - sy;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return;
    const ux = dx / len,
      uy = dy / len; // unit along the bed
    const px = -uy,
      py = ux; // unit perpendicular
    const halfBed = bedW / 2;
    // BED — drawn as a filled quad so it stays opaque under the
    // rails (a stroked line would leave thin gaps at the rails'
    // alpha edges on some renderers).
    g.poly([
      sx + px * halfBed,
      sy + py * halfBed,
      ex + px * halfBed,
      ey + py * halfBed,
      ex - px * halfBed,
      ey - py * halfBed,
      sx - px * halfBed,
      sy - py * halfBed,
    ]);
    g.fill({ color: TRAINSIM_TRACK_PALETTE.BED, alpha });

    // RAILS — two thin parallelograms offset perpendicular by ±rg.
    for (const sign of [-1, 1]) {
      const ox = px * rg * sign,
        oy = py * rg * sign;
      const halfRw = rw / 2;
      g.poly([
        sx + ox + px * halfRw,
        sy + oy + py * halfRw,
        ex + ox + px * halfRw,
        ey + oy + py * halfRw,
        ex + ox - px * halfRw,
        ey + oy - py * halfRw,
        sx + ox - px * halfRw,
        sy + oy - py * halfRw,
      ]);
      g.fill({ color: TRAINSIM_TRACK_PALETTE.RAIL, alpha });
    }

    // TIES — two perpendicular crossbars at 1/4 and 3/4 of the bed.
    // Each tie matches the cardinal style: dark ends + cream middle.
    const halfTs = ts / 2;
    const halfTw = tw / 2;
    for (const tFrac of [0.25, 0.75]) {
      const tcx = sx + dx * tFrac;
      const tcy = sy + dy * tFrac;
      // Tie axis is perpendicular to the bed (px, py). Walk from
      // -halfTs to +halfTs along that axis, splitting into three
      // segments (end / mid / end). To avoid building a poly per
      // segment, we paint the whole tie as one bed strip then
      // overlay the cream middle.
      const a = { x: tcx + px * halfTs, y: tcy + py * halfTs };
      const b = { x: tcx - px * halfTs, y: tcy - py * halfTs };
      // Full tie (end colour).
      g.poly([
        a.x + ux * halfTw,
        a.y + uy * halfTw,
        b.x + ux * halfTw,
        b.y + uy * halfTw,
        b.x - ux * halfTw,
        b.y - uy * halfTw,
        a.x - ux * halfTw,
        a.y - uy * halfTw,
      ]);
      g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_END, alpha });
      // Middle band (cream).
      const midLen = halfTs - capLen;
      const mA = { x: tcx + px * midLen, y: tcy + py * midLen };
      const mB = { x: tcx - px * midLen, y: tcy - py * midLen };
      g.poly([
        mA.x + ux * halfTw,
        mA.y + uy * halfTw,
        mB.x + ux * halfTw,
        mB.y + uy * halfTw,
        mB.x - ux * halfTw,
        mB.y - uy * halfTw,
        mA.x - ux * halfTw,
        mA.y - uy * halfTw,
      ]);
      g.fill({ color: TRAINSIM_TRACK_PALETTE.TIE_MID, alpha });
    }
  };

  // Full diagonal: both opposite corners open → corner-to-corner.
  // Half diagonal: only one corner open → corner-to-centre, used by
  // the `bendr` / `bendl` tiles where a half-cardinal stroke meets a
  // half-diagonal stroke at the cell centre to draw a 45° bend.
  if (ne && sw) drawDiagonal(cs, 0, 0, cs);
  else if (ne) drawDiagonal(cs, 0, c, c);
  else if (sw) drawDiagonal(0, cs, c, c);

  if (nw && se) drawDiagonal(0, 0, cs, cs);
  else if (nw) drawDiagonal(0, 0, c, c);
  else if (se) drawDiagonal(cs, cs, c, c);
}

/**
 * Build an SVG <svg> string showing a track tile at the given type +
 * rotation. Mirrors `drawTrainsimTrack` shape-for-shape so the palette
 * thumbnails in the stage editor read identically to how the tile will
 * paint on the board (just smaller). The output has no embedded
 * background — the host element supplies the dark mat behind it.
 *
 * @param {keyof typeof TILE_DEFS} type
 * @param {number} [rot=0] quarter-turns (0..3)
 * @param {{ size?: number }} [opts]  px viewBox, defaults to 60
 * @returns {string} self-contained `<svg>...</svg>` markup
 */
export function buildTrainsimTrackSvg(type, rot = 0, opts = {}) {
  const cs = opts.size || 60;
  const def = TILE_DEFS[type];
  if (!def) return "";
  const conn = rotateConnections(def, rot);

  const rg = Math.round(cs * 0.22);
  const rw = Math.max(2, Math.round(cs * 0.07));
  const tw = Math.max(3, Math.round(cs * 0.18));
  const ts = rg * 2 + rw * 6;
  const c = cs / 2;
  const bedW = rg * 2 + rw;
  const capLen = Math.max(1, Math.round(ts * 0.25));
  const TIE_OFFSETS = [cs / 4, (3 * cs) / 4];

  const BED = "#" + TRAINSIM_TRACK_PALETTE.BED.toString(16).padStart(6, "0");
  const RAIL = "#" + TRAINSIM_TRACK_PALETTE.RAIL.toString(16).padStart(6, "0");
  const TIE_MID =
    "#" + TRAINSIM_TRACK_PALETTE.TIE_MID.toString(16).padStart(6, "0");
  const TIE_END =
    "#" + TRAINSIM_TRACK_PALETTE.TIE_END.toString(16).padStart(6, "0");

  const n = conn[0],
    e = conn[2],
    s = conn[4],
    w = conn[6];
  const ne = conn[1],
    se = conn[3],
    sw = conn[5],
    nw = conn[7];

  const parts = [];
  const rect = (x, y, w_, h_, color) =>
    parts.push(
      `<rect x="${x}" y="${y}" width="${w_}" height="${h_}" fill="${color}"/>`,
    );
  const poly = (pts, color) =>
    parts.push(
      `<polygon points="${pts.map((p) => `${p[0]},${p[1]}`).join(" ")}" fill="${color}"/>`,
    );

  const drawTieH = (cx, cy) => {
    const left = cx - ts / 2;
    const top = cy - tw / 2;
    rect(left, top, capLen, tw, TIE_END);
    rect(left + capLen, top, ts - capLen * 2, tw, TIE_MID);
    rect(left + ts - capLen, top, capLen, tw, TIE_END);
  };
  const drawTieV = (cx, cy) => {
    const left = cx - tw / 2;
    const top = cy - ts / 2;
    rect(left, top, tw, capLen, TIE_END);
    rect(left, top + capLen, tw, ts - capLen * 2, TIE_MID);
    rect(left, top + ts - capLen, tw, capLen, TIE_END);
  };

  const nsThru = n && s;
  const ewThru = e && w;

  // Curve tile — mirrors `drawTrainsimTrack`'s quarter-annulus
  // branch so palette thumbnails look identical to in-game corners.
  const cardinalCount = (n ? 1 : 0) + (e ? 1 : 0) + (s ? 1 : 0) + (w ? 1 : 0);
  const anyDiagonal = ne || se || sw || nw;
  const isCurve = cardinalCount === 2 && !nsThru && !ewThru && !anyDiagonal;
  if (isCurve) {
    let acx = 0,
      acy = 0,
      startAng = 0,
      endAng = 0;
    if (n && e) {
      acx = cs;
      acy = 0;
      startAng = Math.PI;
      endAng = Math.PI / 2;
    }
    if (e && s) {
      acx = cs;
      acy = cs;
      startAng = -Math.PI / 2;
      endAng = -Math.PI;
    }
    if (s && w) {
      acx = 0;
      acy = cs;
      startAng = 0;
      endAng = -Math.PI / 2;
    }
    if (w && n) {
      acx = 0;
      acy = 0;
      startAng = Math.PI / 2;
      endAng = 0;
    }

    const rOuter = c + bedW / 2;
    const rInner = c - bedW / 2;
    const rRailL = c - rg;
    const rRailR = c + rg;
    const rCentre = c;
    const SEGS = 24;
    const lerp = (t) => startAng + (endAng - startAng) * t;

    const bedPts = [];
    for (let i = 0; i <= SEGS; i++) {
      const a = lerp(i / SEGS);
      bedPts.push([acx + Math.cos(a) * rOuter, acy + Math.sin(a) * rOuter]);
    }
    for (let i = SEGS; i >= 0; i--) {
      const a = lerp(i / SEGS);
      bedPts.push([acx + Math.cos(a) * rInner, acy + Math.sin(a) * rInner]);
    }
    poly(bedPts, BED);

    const railStripe = (rMid) => {
      const r1 = rMid - rw / 2;
      const r2 = rMid + rw / 2;
      for (let i = 0; i < SEGS; i++) {
        const a1 = lerp(i / SEGS);
        const a2 = lerp((i + 1) / SEGS);
        poly(
          [
            [acx + Math.cos(a1) * r1, acy + Math.sin(a1) * r1],
            [acx + Math.cos(a2) * r1, acy + Math.sin(a2) * r1],
            [acx + Math.cos(a2) * r2, acy + Math.sin(a2) * r2],
            [acx + Math.cos(a1) * r2, acy + Math.sin(a1) * r2],
          ],
          RAIL,
        );
      }
    };
    railStripe(rRailL);
    railStripe(rRailR);

    const halfTs = ts / 2;
    const halfTw = tw / 2;
    for (const tFrac of [0.25, 0.75]) {
      const a = lerp(tFrac);
      const tcx = acx + Math.cos(a) * rCentre;
      const tcy = acy + Math.sin(a) * rCentre;
      const rx = Math.cos(a);
      const ry = Math.sin(a);
      const tx = -Math.sin(a);
      const ty = Math.cos(a);
      const aPt = { x: tcx + rx * halfTs, y: tcy + ry * halfTs };
      const bPt = { x: tcx - rx * halfTs, y: tcy - ry * halfTs };
      poly(
        [
          [aPt.x + tx * halfTw, aPt.y + ty * halfTw],
          [bPt.x + tx * halfTw, bPt.y + ty * halfTw],
          [bPt.x - tx * halfTw, bPt.y - ty * halfTw],
          [aPt.x - tx * halfTw, aPt.y - ty * halfTw],
        ],
        TIE_END,
      );
      const midLen = halfTs - capLen;
      const mA = { x: tcx + rx * midLen, y: tcy + ry * midLen };
      const mB = { x: tcx - rx * midLen, y: tcy - ry * midLen };
      poly(
        [
          [mA.x + tx * halfTw, mA.y + ty * halfTw],
          [mB.x + tx * halfTw, mB.y + ty * halfTw],
          [mB.x - tx * halfTw, mB.y - ty * halfTw],
          [mA.x - tx * halfTw, mA.y - ty * halfTw],
        ],
        TIE_MID,
      );
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cs} ${cs}" width="100%" height="100%">${parts.join("")}</svg>`;
  }

  if (nsThru) {
    rect(c - bedW / 2, 0, bedW, cs, BED);
    for (const off of TIE_OFFSETS) drawTieH(c, off);
    rect(c - rg - rw / 2, 0, rw, cs, RAIL);
    rect(c + rg - rw / 2, 0, rw, cs, RAIL);
  }
  if (ewThru) {
    rect(0, c - bedW / 2, cs, bedW, BED);
    for (const off of TIE_OFFSETS) drawTieV(off, c);
    rect(0, c - rg - rw / 2, cs, rw, RAIL);
    rect(0, c + rg - rw / 2, cs, rw, RAIL);
  }
  if (!nsThru && n) {
    rect(c - bedW / 2, 0, bedW, c, BED);
    drawTieH(c, TIE_OFFSETS[0]);
    rect(c - rg - rw / 2, 0, rw, c, RAIL);
    rect(c + rg - rw / 2, 0, rw, c, RAIL);
  }
  if (!nsThru && s) {
    rect(c - bedW / 2, c, bedW, c, BED);
    drawTieH(c, TIE_OFFSETS[1]);
    rect(c - rg - rw / 2, c, rw, c, RAIL);
    rect(c + rg - rw / 2, c, rw, c, RAIL);
  }
  if (!ewThru && e) {
    rect(c, c - bedW / 2, c, bedW, BED);
    drawTieV(TIE_OFFSETS[1], c);
    rect(c, c - rg - rw / 2, c, rw, RAIL);
    rect(c, c + rg - rw / 2, c, rw, RAIL);
  }
  if (!ewThru && w) {
    rect(0, c - bedW / 2, c, bedW, BED);
    drawTieV(TIE_OFFSETS[0], c);
    rect(0, c - rg - rw / 2, c, rw, RAIL);
    rect(0, c + rg - rw / 2, c, rw, RAIL);
  }
  const anyCardinal = n || e || s || w;
  if (!nsThru && !ewThru && anyCardinal) {
    parts.push(
      `<circle cx="${c}" cy="${c}" r="${rg + rw * 2}" fill="${TIE_END}"/>`,
    );
  }

  // Diagonals — same parallelogram-based painting as drawTrainsimTrack.
  const drawDiagSvg = (sx, sy, ex, ey) => {
    const dx = ex - sx,
      dy = ey - sy;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return;
    const ux = dx / len,
      uy = dy / len;
    const px = -uy,
      py = ux;
    const halfBed = bedW / 2;
    poly(
      [
        [sx + px * halfBed, sy + py * halfBed],
        [ex + px * halfBed, ey + py * halfBed],
        [ex - px * halfBed, ey - py * halfBed],
        [sx - px * halfBed, sy - py * halfBed],
      ],
      BED,
    );
    for (const sign of [-1, 1]) {
      const ox = px * rg * sign,
        oy = py * rg * sign;
      const halfRw = rw / 2;
      poly(
        [
          [sx + ox + px * halfRw, sy + oy + py * halfRw],
          [ex + ox + px * halfRw, ey + oy + py * halfRw],
          [ex + ox - px * halfRw, ey + oy - py * halfRw],
          [sx + ox - px * halfRw, sy + oy - py * halfRw],
        ],
        RAIL,
      );
    }
    const halfTs = ts / 2;
    const halfTw = tw / 2;
    for (const tFrac of [0.25, 0.75]) {
      const tcx = sx + dx * tFrac;
      const tcy = sy + dy * tFrac;
      const a = { x: tcx + px * halfTs, y: tcy + py * halfTs };
      const b = { x: tcx - px * halfTs, y: tcy - py * halfTs };
      poly(
        [
          [a.x + ux * halfTw, a.y + uy * halfTw],
          [b.x + ux * halfTw, b.y + uy * halfTw],
          [b.x - ux * halfTw, b.y - uy * halfTw],
          [a.x - ux * halfTw, a.y - uy * halfTw],
        ],
        TIE_END,
      );
      const midLen = halfTs - capLen;
      const mA = { x: tcx + px * midLen, y: tcy + py * midLen };
      const mB = { x: tcx - px * midLen, y: tcy - py * midLen };
      poly(
        [
          [mA.x + ux * halfTw, mA.y + uy * halfTw],
          [mB.x + ux * halfTw, mB.y + uy * halfTw],
          [mB.x - ux * halfTw, mB.y - uy * halfTw],
          [mA.x - ux * halfTw, mA.y - uy * halfTw],
        ],
        TIE_MID,
      );
    }
  };
  // Full + half diagonals — mirrors the painter so palette
  // thumbnails of `bendr` / `bendl` show the same half-diagonal
  // running from the corner to the cell centre.
  if (ne && sw) drawDiagSvg(cs, 0, 0, cs);
  else if (ne) drawDiagSvg(cs, 0, c, c);
  else if (sw) drawDiagSvg(0, cs, c, c);

  if (nw && se) drawDiagSvg(0, 0, cs, cs);
  else if (nw) drawDiagSvg(0, 0, c, c);
  else if (se) drawDiagSvg(cs, cs, c, c);

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cs} ${cs}" width="100%" height="100%">${parts.join("")}</svg>`;
}

/**
 * Draw a road tile (asphalt + edge stripes + dashed centreline).
 *
 * Same calling convention as `drawTrainsimTrack`: caller positions
 * the Graphics object at the tile's origin, we paint inside the
 * (0,0)..(cs,cs) box. Connection encoding is identical to the
 * 8-direction port system used by tracks; cars share `getExitDir`
 * with trains so the routing logic is symmetric.
 *
 * Visual recipe per direction the cell opens in:
 *   - Fill the asphalt half-strip from the cell centre to that edge
 *   - Paint two cream "edge lines" along the long sides of the strip
 *   - Paint a single dashed yellow centreline down the middle
 * Through-tiles (N+S or E+W open) draw a full-cell strip in one
 * pass. Junctions composite multiple half-strips, with a central
 * disc of asphalt covering the seam where they meet.
 *
 * @param {import('../../../vendor/pixi.min.mjs').Graphics} g
 * @param {number} cs cell size in pixels
 * @param {boolean[]} conn 8-element rotated connections
 * @param {{ alpha?: number, bgColor?: number }} [options]
 */
export function drawTrainsimRoad(g, cs, conn, options = {}) {
  const { alpha = 1, bgColor = null, worldX = 0, worldY = 0 } = options;

  if (bgColor != null) {
    g.roundRect(1, 1, cs - 2, cs - 2, 4);
    g.fill({ color: bgColor, alpha });
  }

  const n = conn[0],
    e = conn[2],
    s = conn[4],
    w = conn[6];
  if (!(n || e || s || w)) return;

  const c = cs / 2;
  const roadW = Math.round(cs * 1.05); // full carriageway width
  const halfRoad = roadW / 2;
  const edgeW = Math.max(1, Math.round(cs * 0.04));
  const centreW = Math.max(1, Math.round(cs * 0.04));
  const dashLen = Math.max(2, Math.round(cs * 0.16));
  const dashGap = Math.max(2, Math.round(cs * 0.1));
  // Dashes are anchored to a GLOBAL grid (period = dashLen + dashGap)
  // so adjacent tiles continue the same pattern instead of each
  // restarting its own phase at the cell boundary. Without this
  // the road centreline looked nasty — visible "seams" every cs
  // pixels where the truncated end-of-tile dash met a fresh
  // start-of-tile dash, sometimes overlapping, sometimes leaving
  // a wider gap. Phase alignment makes the dashed line read as one
  // continuous stripe down the length of the road.
  const dashPeriod = dashLen + dashGap;

  const A = TRAINSIM_ROAD_PALETTE.ASPHALT;
  const EL = TRAINSIM_ROAD_PALETTE.EDGE_LINE;
  const CL = TRAINSIM_ROAD_PALETTE.CENTRELINE;

  const nsThru = n && s;
  const ewThru = e && w;

  // ── Curve tile (exactly two perpendicular cardinal ports) ──
  // Real roads BEND at corners — they don't form a sharp L with a
  // visible square notch in the inner edge stripe. Detect the four
  // curve configurations (N+E, E+S, S+W, W+N) and render the cell
  // as a quarter-annulus instead of two perpendicular half-strips.
  // The arc's centre sits at the cell corner FARTHEST from both
  // open ports — i.e. the corner the road bends AROUND — so the
  // outer arc has radius `c + halfRoad` and the inner arc has
  // radius `c - halfRoad`. At the cell edges those radii line up
  // exactly with the road's straight-tile edges (because cs/2 ±
  // halfRoad is what the straight tile uses), so straights and
  // curves stitch together without seams.
  const cardinalCount = (n ? 1 : 0) + (e ? 1 : 0) + (s ? 1 : 0) + (w ? 1 : 0);
  const isCurve = cardinalCount === 2 && !nsThru && !ewThru;
  if (isCurve) {
    let cx, cy, startAng, endAng;
    if (n && e) {
      cx = cs;
      cy = 0;
      startAng = Math.PI;
      endAng = Math.PI / 2;
    }
    if (e && s) {
      cx = cs;
      cy = cs;
      startAng = -Math.PI / 2;
      endAng = -Math.PI;
    }
    if (s && w) {
      cx = 0;
      cy = cs;
      startAng = 0;
      endAng = -Math.PI / 2;
    }
    if (w && n) {
      cx = 0;
      cy = 0;
      startAng = Math.PI / 2;
      endAng = 0;
    }

    const rOuter = c + halfRoad;
    const rInner = c - halfRoad;
    const rCentre = c;

    // 24 segments per quarter-arc — smooth enough that even at
    // 60 px cells the curve reads as a clean circle, but cheap
    // enough that a stage with hundreds of corner tiles doesn't
    // tank the GPU vertex count.
    const SEGS = 24;
    const lerp = (t) => startAng + (endAng - startAng) * t;

    // Asphalt fill: outer arc forward, then inner arc back.
    const fillPts = [];
    for (let i = 0; i <= SEGS; i++) {
      const a = lerp(i / SEGS);
      fillPts.push(cx + Math.cos(a) * rOuter, cy + Math.sin(a) * rOuter);
    }
    for (let i = SEGS; i >= 0; i--) {
      const a = lerp(i / SEGS);
      fillPts.push(cx + Math.cos(a) * rInner, cy + Math.sin(a) * rInner);
    }
    g.poly(fillPts);
    g.fill({ color: A, alpha });

    // Cream edge stripes — outer and inner arc walls. Drawn as a
    // ring of thin trapezoidal quads (one per arc segment) so the
    // stripe follows the curve smoothly. Width = edgeW, anchored
    // INWARD from each radius so the asphalt body still extends
    // to its full nominal width.
    const ringStripe = (radius, inward) => {
      const r1 = inward ? radius - edgeW : radius;
      const r2 = inward ? radius : radius + edgeW;
      for (let i = 0; i < SEGS; i++) {
        const a1 = lerp(i / SEGS);
        const a2 = lerp((i + 1) / SEGS);
        g.poly([
          cx + Math.cos(a1) * r1,
          cy + Math.sin(a1) * r1,
          cx + Math.cos(a2) * r1,
          cy + Math.sin(a2) * r1,
          cx + Math.cos(a2) * r2,
          cy + Math.sin(a2) * r2,
          cx + Math.cos(a1) * r2,
          cy + Math.sin(a1) * r2,
        ]);
        g.fill({ color: EL, alpha });
      }
    };
    ringStripe(rOuter, true); // outer wall, stripe sits INSIDE the asphalt
    ringStripe(rInner, false); // inner wall, stripe sits OUTSIDE the asphalt

    // Dashed centreline along the arc. Convert the dash + gap
    // pattern from pixels to angular increments at the centreline
    // radius, then pack as many evenly-spaced dashes as fit into
    // the quarter-arc with equal padding on both sides. (Trying
    // to globally phase-align with adjacent straight tiles would
    // need an arc-length world distance — overkill for a curve
    // tile that's bounded; symmetrical placement reads cleanly
    // and the slight phase shift at a straight↔curve transition
    // looks natural for a road.)
    const totalAng = Math.abs(endAng - startAng);
    const dashAngLen = dashLen / rCentre;
    const dashAngStep = (dashLen + dashGap) / rCentre;
    const sign = endAng >= startAng ? 1 : -1;
    const dashCount = Math.max(
      1,
      Math.floor((totalAng + dashAngStep - dashAngLen) / dashAngStep),
    );
    const usedAng =
      dashCount * dashAngLen + (dashCount - 1) * (dashAngStep - dashAngLen);
    const startPadding = (totalAng - usedAng) / 2;
    for (let i = 0; i < dashCount; i++) {
      const a1 = startAng + sign * (startPadding + i * dashAngStep);
      const a2 = a1 + sign * dashAngLen;
      const w1 = rCentre - centreW / 2;
      const w2 = rCentre + centreW / 2;
      g.poly([
        cx + Math.cos(a1) * w1,
        cy + Math.sin(a1) * w1,
        cx + Math.cos(a1) * w2,
        cy + Math.sin(a1) * w2,
        cx + Math.cos(a2) * w2,
        cy + Math.sin(a2) * w2,
        cx + Math.cos(a2) * w1,
        cy + Math.sin(a2) * w1,
      ]);
      g.fill({ color: CL, alpha });
    }
    return;
  }

  // ── Asphalt body (laid first so stripes paint on top) ──────
  // Full-cell strips for through-tiles, half-cell strips for
  // dead-ends and junction branches. The centre square is the
  // intersection's connective tissue.
  if (nsThru) {
    g.rect(c - halfRoad, 0, roadW, cs);
    g.fill({ color: A, alpha });
  } else {
    if (n) {
      g.rect(c - halfRoad, 0, roadW, c);
      g.fill({ color: A, alpha });
    }
    if (s) {
      g.rect(c - halfRoad, c, roadW, c);
      g.fill({ color: A, alpha });
    }
  }
  if (ewThru) {
    g.rect(0, c - halfRoad, cs, roadW);
    g.fill({ color: A, alpha });
  } else {
    if (e) {
      g.rect(c, c - halfRoad, c, roadW);
      g.fill({ color: A, alpha });
    }
    if (w) {
      g.rect(0, c - halfRoad, c, roadW);
      g.fill({ color: A, alpha });
    }
  }
  // Junction disc — masks the seam where perpendicular strips meet.
  if ((n || s) && (e || w)) {
    g.rect(c - halfRoad, c - halfRoad, roadW, roadW);
    g.fill({ color: A, alpha });
  }

  // ── Edge lines (cream fog lines along outer carriageway edges) ──
  // Skip the edge where another road continues (so two adjacent
  // straight tiles read as one continuous road with no seam).
  //
  // Junctions (T + cross) need special treatment: a naive draw
  // makes the perpendicular through-stripes overlap inside the
  // intersection and form a visible inner `#` box. The fix is to
  // route every junction stripe through `drawEdges` per-port, each
  // terminating at the intersection box, then drop the through-axis
  // FAR-SIDE stripe of a T as one continuous span (since no branch
  // breaks it on that side). `cardinalCount` was computed earlier
  // for curve detection.
  const isCross = cardinalCount === 4;
  const isTee = cardinalCount === 3;
  const drawEdges = () => {
    if (isCross || isTee) {
      // Each open port = two short cream stripes from the cell
      // edge up to the intersection box (length c - halfRoad).
      // Inside the box: nothing — the asphalt reads as a clean
      // open intersection, not a stripe cage.
      if (n) {
        g.rect(c - halfRoad, 0, edgeW, c - halfRoad);
        g.fill({ color: EL, alpha });
        g.rect(c + halfRoad - edgeW, 0, edgeW, c - halfRoad);
        g.fill({ color: EL, alpha });
      }
      if (s) {
        g.rect(c - halfRoad, c + halfRoad, edgeW, c - halfRoad);
        g.fill({ color: EL, alpha });
        g.rect(c + halfRoad - edgeW, c + halfRoad, edgeW, c - halfRoad);
        g.fill({ color: EL, alpha });
      }
      if (e) {
        g.rect(c + halfRoad, c - halfRoad, c - halfRoad, edgeW);
        g.fill({ color: EL, alpha });
        g.rect(c + halfRoad, c + halfRoad - edgeW, c - halfRoad, edgeW);
        g.fill({ color: EL, alpha });
      }
      if (w) {
        g.rect(0, c - halfRoad, c - halfRoad, edgeW);
        g.fill({ color: EL, alpha });
        g.rect(0, c + halfRoad - edgeW, c - halfRoad, edgeW);
        g.fill({ color: EL, alpha });
      }
      // T-junction: the far-side edge of the through road is
      // continuous because there's no branch breaking it there.
      // We OVER-draw the segmented version with one full-cell
      // stripe on that side — same colour so no seam.
      if (isTee) {
        if (n && e && w && !s) {
          g.rect(0, c + halfRoad - edgeW, cs, edgeW);
          g.fill({ color: EL, alpha });
        } else if (s && e && w && !n) {
          g.rect(0, c - halfRoad, cs, edgeW);
          g.fill({ color: EL, alpha });
        } else if (e && n && s && !w) {
          g.rect(c - halfRoad, 0, edgeW, cs);
          g.fill({ color: EL, alpha });
        } else if (w && n && s && !e) {
          g.rect(c + halfRoad - edgeW, 0, edgeW, cs);
          g.fill({ color: EL, alpha });
        }
      }
      // Tiny cream curb fillets at the inner intersection
      // corners. A fillet exists only where two ADJACENT ports
      // are open (so two cream stripes actually meet there).
      // Drawn as a small quarter-disc that hugs the L corner —
      // rounds the visual transition without changing the road
      // geometry. Radius scaled to edgeW so it reads as a "curb
      // radius", not a freeway-scale sweep.
      const fr = Math.max(edgeW * 1.3, Math.round(cs * 0.05));
      const fillet = (px, py, ang0) => {
        const SEGS = 6;
        const pts = [px, py];
        for (let i = 0; i <= SEGS; i++) {
          const a = ang0 + (Math.PI / 2) * (i / SEGS);
          pts.push(px + Math.cos(a) * fr, py + Math.sin(a) * fr);
        }
        g.poly(pts);
        g.fill({ color: EL, alpha });
      };
      // NE inner corner: between N and E ports
      if (n && e) fillet(c + halfRoad, c - halfRoad, Math.PI / 2);
      // SE inner corner: between S and E ports
      if (s && e) fillet(c + halfRoad, c + halfRoad, Math.PI);
      // SW inner corner: between S and W ports
      if (s && w) fillet(c - halfRoad, c + halfRoad, -Math.PI / 2);
      // NW inner corner: between N and W ports
      if (n && w) fillet(c - halfRoad, c - halfRoad, 0);
      return;
    }

    if (nsThru) {
      g.rect(c - halfRoad, 0, edgeW, cs);
      g.fill({ color: EL, alpha });
      g.rect(c + halfRoad - edgeW, 0, edgeW, cs);
      g.fill({ color: EL, alpha });
    }
    if (ewThru) {
      g.rect(0, c - halfRoad, cs, edgeW);
      g.fill({ color: EL, alpha });
      g.rect(0, c + halfRoad - edgeW, cs, edgeW);
      g.fill({ color: EL, alpha });
    }
    // Half-strip edges for branches / dead-ends.
    if (!nsThru && n) {
      g.rect(c - halfRoad, 0, edgeW, c);
      g.fill({ color: EL, alpha });
      g.rect(c + halfRoad - edgeW, 0, edgeW, c);
      g.fill({ color: EL, alpha });
    }
    if (!nsThru && s) {
      g.rect(c - halfRoad, c, edgeW, c);
      g.fill({ color: EL, alpha });
      g.rect(c + halfRoad - edgeW, c, edgeW, c);
      g.fill({ color: EL, alpha });
    }
    if (!ewThru && e) {
      g.rect(c, c - halfRoad, c, edgeW);
      g.fill({ color: EL, alpha });
      g.rect(c, c + halfRoad - edgeW, c, edgeW);
      g.fill({ color: EL, alpha });
    }
    if (!ewThru && w) {
      g.rect(0, c - halfRoad, c, edgeW);
      g.fill({ color: EL, alpha });
      g.rect(0, c + halfRoad - edgeW, c, edgeW);
      g.fill({ color: EL, alpha });
    }
  };
  drawEdges();

  // ── Dashed centreline ─────────────────────────────────────
  // One dashed stripe per through-axis. Junctions get a small
  // central gap so the lines don't pile up at the intersection.
  //
  // Phase is anchored to world coords (worldX/worldY + the local
  // stripe origin) modulo `dashPeriod`, so dashes line up across
  // adjacent tiles. A dash that straddles a cell boundary is
  // CLIPPED to the cell on each side rather than truncated and
  // restarted — the two halves meet seamlessly because both halves
  // are computed from the same global anchor.
  const dashStripe = (x, y, w, h, vertical) => {
    const len = vertical ? h : w;
    if (len <= 0) return;
    const worldStart = vertical ? worldY + y : worldX + x;
    // Offset of the first global dash start at or before `worldStart`.
    // We may start one period BEFORE the stripe so a dash that
    // straddles the leading edge still gets its visible half drawn.
    let pos = -(((worldStart % dashPeriod) + dashPeriod) % dashPeriod);
    while (pos < len) {
      const segStart = Math.max(0, pos);
      const segEnd = Math.min(len, pos + dashLen);
      if (segEnd > segStart) {
        if (vertical) {
          g.rect(x, y + segStart, w, segEnd - segStart);
        } else {
          g.rect(x + segStart, y, segEnd - segStart, h);
        }
        g.fill({ color: CL, alpha });
      }
      pos += dashPeriod;
    }
  };
  // Cross-roads need a small "lead-in" of dashes INTO the intersection
  // so the cell doesn't read as a bare asphalt box. Each branch's dashes
  // continue past the intersection box edge by `crossDashLeadIn` pixels,
  // stopping well short of the centre so the four leads don't pile up.
  const crossDashLeadIn = Math.round(halfRoad * 0.45);
  if (nsThru) {
    if (ewThru) {
      // Cross: dashes on each branch extend slightly into the box,
      // leaving a clean central gap where they would otherwise overlap.
      dashStripe(
        c - centreW / 2,
        0,
        centreW,
        c - halfRoad + crossDashLeadIn,
        true,
      );
      dashStripe(
        c - centreW / 2,
        c + halfRoad - crossDashLeadIn,
        centreW,
        c - halfRoad + crossDashLeadIn,
        true,
      );
    } else {
      dashStripe(c - centreW / 2, 0, centreW, cs, true);
    }
  } else {
    if (n)
      dashStripe(
        c - centreW / 2,
        0,
        centreW,
        c - (e || w ? halfRoad : 0),
        true,
      );
    if (s)
      dashStripe(
        c - centreW / 2,
        c + (e || w ? halfRoad : 0),
        centreW,
        c - (e || w ? halfRoad : 0),
        true,
      );
  }
  if (ewThru) {
    if (nsThru) {
      dashStripe(
        0,
        c - centreW / 2,
        c - halfRoad + crossDashLeadIn,
        centreW,
        false,
      );
      dashStripe(
        c + halfRoad - crossDashLeadIn,
        c - centreW / 2,
        c - halfRoad + crossDashLeadIn,
        centreW,
        false,
      );
    } else {
      dashStripe(0, c - centreW / 2, cs, centreW, false);
    }
  } else {
    if (e)
      dashStripe(
        c + (n || s ? halfRoad : 0),
        c - centreW / 2,
        c - (n || s ? halfRoad : 0),
        centreW,
        false,
      );
    if (w)
      dashStripe(
        0,
        c - centreW / 2,
        c - (n || s ? halfRoad : 0),
        centreW,
        false,
      );
  }
}

/**
 * Build an SVG <svg> string for a road tile thumbnail. Mirrors
 * `drawTrainsimRoad` shape-for-shape so the editor palette preview
 * reads identically to what gets painted on the board (just smaller).
 *
 * @param {keyof typeof ROAD_TILE_DEFS} type
 * @param {number} [rot=0]
 * @param {{ size?: number }} [opts]
 * @returns {string}
 */
export function buildTrainsimRoadSvg(type, rot = 0, opts = {}) {
  const cs = opts.size || 60;
  const def = ROAD_TILE_DEFS[type];
  if (!def) return "";
  const conn = rotateConnections(def, rot);

  const c = cs / 2;
  const roadW = Math.round(cs * 1.05);
  const halfRoad = roadW / 2;
  const edgeW = Math.max(1, Math.round(cs * 0.04));
  const centreW = Math.max(1, Math.round(cs * 0.04));
  const dashLen = Math.max(2, Math.round(cs * 0.16));
  const dashGap = Math.max(2, Math.round(cs * 0.1));

  const A = "#" + TRAINSIM_ROAD_PALETTE.ASPHALT.toString(16).padStart(6, "0");
  const EL =
    "#" + TRAINSIM_ROAD_PALETTE.EDGE_LINE.toString(16).padStart(6, "0");
  const CL =
    "#" + TRAINSIM_ROAD_PALETTE.CENTRELINE.toString(16).padStart(6, "0");

  const n = conn[0],
    e = conn[2],
    s = conn[4],
    w = conn[6];

  const parts = [];
  const rect = (x, y, w_, h_, color) =>
    parts.push(
      `<rect x="${x}" y="${y}" width="${w_}" height="${h_}" fill="${color}"/>`,
    );
  const poly = (pts, color) =>
    parts.push(
      `<polygon points="${pts.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(" ")}" fill="${color}"/>`,
    );

  const nsThru = n && s;
  const ewThru = e && w;

  // Curve thumbnail — same arc geometry as drawTrainsimRoad so the
  // palette preview reads identically to the in-game tile.
  const cardinalCount = (n ? 1 : 0) + (e ? 1 : 0) + (s ? 1 : 0) + (w ? 1 : 0);
  const isCurve = cardinalCount === 2 && !nsThru && !ewThru;
  if (isCurve) {
    let cx, cy, startAng, endAng;
    if (n && e) {
      cx = cs;
      cy = 0;
      startAng = Math.PI;
      endAng = Math.PI / 2;
    }
    if (e && s) {
      cx = cs;
      cy = cs;
      startAng = -Math.PI / 2;
      endAng = -Math.PI;
    }
    if (s && w) {
      cx = 0;
      cy = cs;
      startAng = 0;
      endAng = -Math.PI / 2;
    }
    if (w && n) {
      cx = 0;
      cy = 0;
      startAng = Math.PI / 2;
      endAng = 0;
    }

    const rOuter = c + halfRoad;
    const rInner = c - halfRoad;
    const rCentre = c;
    const SEGS = 24;
    const lerp = (t) => startAng + (endAng - startAng) * t;

    const fillPts = [];
    for (let i = 0; i <= SEGS; i++) {
      const a = lerp(i / SEGS);
      fillPts.push([cx + Math.cos(a) * rOuter, cy + Math.sin(a) * rOuter]);
    }
    for (let i = SEGS; i >= 0; i--) {
      const a = lerp(i / SEGS);
      fillPts.push([cx + Math.cos(a) * rInner, cy + Math.sin(a) * rInner]);
    }
    poly(fillPts, A);

    const ringStripe = (radius, inward) => {
      const r1 = inward ? radius - edgeW : radius;
      const r2 = inward ? radius : radius + edgeW;
      for (let i = 0; i < SEGS; i++) {
        const a1 = lerp(i / SEGS);
        const a2 = lerp((i + 1) / SEGS);
        poly(
          [
            [cx + Math.cos(a1) * r1, cy + Math.sin(a1) * r1],
            [cx + Math.cos(a2) * r1, cy + Math.sin(a2) * r1],
            [cx + Math.cos(a2) * r2, cy + Math.sin(a2) * r2],
            [cx + Math.cos(a1) * r2, cy + Math.sin(a1) * r2],
          ],
          EL,
        );
      }
    };
    ringStripe(rOuter, true);
    ringStripe(rInner, false);

    const totalAng = Math.abs(endAng - startAng);
    const dashAngLen = dashLen / rCentre;
    const dashAngStep = (dashLen + dashGap) / rCentre;
    const sign = endAng >= startAng ? 1 : -1;
    const dashCount = Math.max(
      1,
      Math.floor((totalAng + dashAngStep - dashAngLen) / dashAngStep),
    );
    const usedAng =
      dashCount * dashAngLen + (dashCount - 1) * (dashAngStep - dashAngLen);
    const padding = (totalAng - usedAng) / 2;
    for (let i = 0; i < dashCount; i++) {
      const a1 = startAng + sign * (padding + i * dashAngStep);
      const a2 = a1 + sign * dashAngLen;
      const w1 = rCentre - centreW / 2;
      const w2 = rCentre + centreW / 2;
      poly(
        [
          [cx + Math.cos(a1) * w1, cy + Math.sin(a1) * w1],
          [cx + Math.cos(a1) * w2, cy + Math.sin(a1) * w2],
          [cx + Math.cos(a2) * w2, cy + Math.sin(a2) * w2],
          [cx + Math.cos(a2) * w1, cy + Math.sin(a2) * w1],
        ],
        CL,
      );
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cs} ${cs}" width="100%" height="100%">${parts.join("")}</svg>`;
  }

  if (nsThru) rect(c - halfRoad, 0, roadW, cs, A);
  else {
    if (n) rect(c - halfRoad, 0, roadW, c, A);
    if (s) rect(c - halfRoad, c, roadW, c, A);
  }
  if (ewThru) rect(0, c - halfRoad, cs, roadW, A);
  else {
    if (e) rect(c, c - halfRoad, c, roadW, A);
    if (w) rect(0, c - halfRoad, c, roadW, A);
  }
  if ((n || s) && (e || w)) rect(c - halfRoad, c - halfRoad, roadW, roadW, A);

  // Junction-aware cream edge stripes — see drawTrainsimRoad for full
  // rationale. Branch stripes terminate at the intersection box (no
  // inner # cage), T-junctions get a continuous far-side stripe, and
  // each inner corner where two adjacent ports meet picks up a tiny
  // cream fillet to soften the L termination. `cardinalCount` was
  // computed earlier for curve detection.
  const isCross = cardinalCount === 4;
  const isTee = cardinalCount === 3;
  if (isCross || isTee) {
    if (n) {
      rect(c - halfRoad, 0, edgeW, c - halfRoad, EL);
      rect(c + halfRoad - edgeW, 0, edgeW, c - halfRoad, EL);
    }
    if (s) {
      rect(c - halfRoad, c + halfRoad, edgeW, c - halfRoad, EL);
      rect(c + halfRoad - edgeW, c + halfRoad, edgeW, c - halfRoad, EL);
    }
    if (e) {
      rect(c + halfRoad, c - halfRoad, c - halfRoad, edgeW, EL);
      rect(c + halfRoad, c + halfRoad - edgeW, c - halfRoad, edgeW, EL);
    }
    if (w) {
      rect(0, c - halfRoad, c - halfRoad, edgeW, EL);
      rect(0, c + halfRoad - edgeW, c - halfRoad, edgeW, EL);
    }
    if (isTee) {
      if (n && e && w && !s) rect(0, c + halfRoad - edgeW, cs, edgeW, EL);
      else if (s && e && w && !n) rect(0, c - halfRoad, cs, edgeW, EL);
      else if (e && n && s && !w) rect(c - halfRoad, 0, edgeW, cs, EL);
      else if (w && n && s && !e) rect(c + halfRoad - edgeW, 0, edgeW, cs, EL);
    }
    const fr = Math.max(edgeW * 1.3, Math.round(cs * 0.05));
    const fillet = (px, py, ang0) => {
      const SEGS = 6;
      const pts = [[px, py]];
      for (let i = 0; i <= SEGS; i++) {
        const a = ang0 + (Math.PI / 2) * (i / SEGS);
        pts.push([px + Math.cos(a) * fr, py + Math.sin(a) * fr]);
      }
      poly(pts, EL);
    };
    if (n && e) fillet(c + halfRoad, c - halfRoad, Math.PI / 2);
    if (s && e) fillet(c + halfRoad, c + halfRoad, Math.PI);
    if (s && w) fillet(c - halfRoad, c + halfRoad, -Math.PI / 2);
    if (n && w) fillet(c - halfRoad, c - halfRoad, 0);
  } else {
    if (nsThru) {
      rect(c - halfRoad, 0, edgeW, cs, EL);
      rect(c + halfRoad - edgeW, 0, edgeW, cs, EL);
    }
    if (ewThru) {
      rect(0, c - halfRoad, cs, edgeW, EL);
      rect(0, c + halfRoad - edgeW, cs, edgeW, EL);
    }
    if (!nsThru && n) {
      rect(c - halfRoad, 0, edgeW, c, EL);
      rect(c + halfRoad - edgeW, 0, edgeW, c, EL);
    }
    if (!nsThru && s) {
      rect(c - halfRoad, c, edgeW, c, EL);
      rect(c + halfRoad - edgeW, c, edgeW, c, EL);
    }
    if (!ewThru && e) {
      rect(c, c - halfRoad, c, edgeW, EL);
      rect(c, c + halfRoad - edgeW, c, edgeW, EL);
    }
    if (!ewThru && w) {
      rect(0, c - halfRoad, c, edgeW, EL);
      rect(0, c + halfRoad - edgeW, c, edgeW, EL);
    }
  }

  const dashStripe = (x, y, w, h, vertical) => {
    const len = vertical ? h : w;
    let pos = 0;
    while (pos < len) {
      const remaining = len - pos;
      const seg = Math.min(dashLen, remaining);
      if (vertical) rect(x, y + pos, w, seg, CL);
      else rect(x + pos, y, seg, h, CL);
      pos += seg + dashGap;
    }
  };
  const crossDashLeadIn = Math.round(halfRoad * 0.45);
  if (nsThru) {
    if (ewThru) {
      dashStripe(
        c - centreW / 2,
        0,
        centreW,
        c - halfRoad + crossDashLeadIn,
        true,
      );
      dashStripe(
        c - centreW / 2,
        c + halfRoad - crossDashLeadIn,
        centreW,
        c - halfRoad + crossDashLeadIn,
        true,
      );
    } else {
      dashStripe(c - centreW / 2, 0, centreW, cs, true);
    }
  } else {
    if (n)
      dashStripe(
        c - centreW / 2,
        0,
        centreW,
        c - (e || w ? halfRoad : 0),
        true,
      );
    if (s)
      dashStripe(
        c - centreW / 2,
        c + (e || w ? halfRoad : 0),
        centreW,
        c - (e || w ? halfRoad : 0),
        true,
      );
  }
  if (ewThru) {
    if (nsThru) {
      dashStripe(
        0,
        c - centreW / 2,
        c - halfRoad + crossDashLeadIn,
        centreW,
        false,
      );
      dashStripe(
        c + halfRoad - crossDashLeadIn,
        c - centreW / 2,
        c - halfRoad + crossDashLeadIn,
        centreW,
        false,
      );
    } else {
      dashStripe(0, c - centreW / 2, cs, centreW, false);
    }
  } else {
    if (e)
      dashStripe(
        c + (n || s ? halfRoad : 0),
        c - centreW / 2,
        c - (n || s ? halfRoad : 0),
        centreW,
        false,
      );
    if (w)
      dashStripe(
        0,
        c - centreW / 2,
        c - (n || s ? halfRoad : 0),
        centreW,
        false,
      );
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cs} ${cs}" width="100%" height="100%">${parts.join("")}</svg>`;
}

/**
 * Maps a tile type + rotated connections back to the SkinResolver slot
 * key + rotation needed to draw it.
 *
 * `diagonal` returns `null` — there's no PNG slot for 45° track yet,
 * so the board's `_drawTileGraphics` falls through to the procedural
 * `drawTrainsimTrack` painter, which knows how to render it.
 */
export function getTrackSlotKey(type, connections) {
  if (type === "straight") return "trainsim.track.straight";
  if (type === "curve") return "trainsim.track.corner";
  if (type === "cross") return "trainsim.track.cross";
  if (type === "deadend") return "trainsim.track.deadend";
  if (type === "t") return "trainsim.track.t";
  // Diagonals and 45° bends bypass the PNG slot system entirely —
  // there's no pre-baked artwork for them, so the board falls
  // through to the procedural `drawTrainsimTrack` painter which
  // handles every connection pattern including half-diagonals.
  if (type === "diagonal") return null;
  if (type === "bendr" || type === "bendl") return null;
  if (type === "diagy" || type === "diagy2") return null;
  if (type === "wyec" || type === "wyec2") return null;

  // Same heuristic as Pipeline's board for ambiguous types.
  const cardinals = [
    connections[0],
    connections[2],
    connections[4],
    connections[6],
  ];
  const activeCount = cardinals.filter(Boolean).length;
  if (activeCount === 3) return "trainsim.track.t";
  if (activeCount === 1) return "trainsim.track.deadend";
  return null;
}

/**
 * Rotation (in radians) to apply to the slot sprite so its base
 * orientation lines up with the actual connections. Operates on the
 * cardinal slots (N=0, E=2, S=4, W=6) of the 8-element connection
 * array — diagonal tiles bypass this path entirely (no slot, no
 * sprite rotation; the procedural painter handles the diagonal pose).
 */
export function getTrackRotation(type, connections) {
  const n = connections[0],
    e = connections[2],
    s = connections[4],
    w = connections[6];
  if (type === "straight") {
    return n && s ? Math.PI / 2 : 0;
  }
  if (type === "curve") {
    if (e && s) return Math.PI / 2;
    if (s && w) return Math.PI;
    if (w && n) return Math.PI * 1.5;
    return 0;
  }
  if (type === "cross") return 0;
  if (type === "deadend") {
    if (e) return Math.PI / 2;
    if (s) return Math.PI;
    if (w) return Math.PI * 1.5;
    return 0;
  }
  // t-junction
  if (e && s && w) return Math.PI / 2; // trunk N
  if (s && w && n) return Math.PI; // trunk E
  if (w && n && e) return Math.PI * 1.5; // trunk S
  return 0; // trunk W (default for t)
}

/**
 * Given a switch's base type ('t' or 'cross') and rotation, derive the
 * two connection states (A and B) the switch toggles between.
 *
 * For 't':  state A = straight through the trunk, state B = corner via
 *           the branch. Both states are subsets of the full T's 3 open
 *           directions, so trains coming from a side closed in the
 *           current state crash on entry (per the chosen mechanics).
 *
 * For 'cross': state A = horizontal pass-through (E-W), state B =
 *           vertical pass-through (N-S). Crossings without state are
 *           just `tile.type === 'cross'`.
 */
export function deriveSwitchStates(baseType, rot) {
  const base = rotateConnections(TILE_DEFS[baseType] || TILE_DEFS.t, rot);
  // Helper — promote a length-4 cardinal mask [N, E, S, W] into the
  // 8-element layout (diagonals always false for these switches).
  const card = ([n, e, s, w]) => [n, false, e, false, s, false, w, false];

  if (baseType === "cross") {
    return {
      stateA: card([false, base[2], false, base[6]]), // horizontal E-W
      stateB: card([base[0], false, base[4], false]), // vertical   N-S
    };
  }
  // T-junction: find the closed direction (trunk side), then split into
  // straight (the two collinear opens) and corner (one collinear + branch).
  const n = base[0],
    e = base[2],
    s = base[4],
    w = base[6];
  let straight, corner;
  if (!w) {
    // Closed W → opens are N, E, S. Straight = N-S, branch = E.
    straight = [true, false, true, false];
    corner = [false, true, true, false]; // S-E corner
  } else if (!e) {
    // Closed E → opens are N, S, W. Straight = N-S, branch = W.
    straight = [true, false, true, false];
    corner = [false, false, true, true]; // S-W corner
  } else if (!n) {
    // Closed N → opens are E, S, W. Straight = E-W, branch = S.
    straight = [false, true, false, true];
    corner = [false, true, true, false]; // E-S corner
  } else {
    // Closed S → opens are N, E, W. Straight = E-W, branch = N.
    straight = [false, true, false, true];
    corner = [true, true, false, false]; // N-E corner
  }
  return { stateA: card(straight), stateB: card(corner) };
}

/**
 * Derive a switch's complete LIST of ROUTING TABLES — one per
 * "interesting" way to wire up the junction's open ports.
 *
 * A switch never closes a port. From any open entry the train must
 * still go somewhere; the switch just picks WHICH exit. We enumerate
 * routings keyed by a chosen "main pair" of ports that are connected
 * straight through; any extra open ports route to whichever member
 * of the main pair makes the gentler turn for an arriving train.
 *
 * Result: the player gets ONE state per possible main-pair pairing,
 * giving:
 *
 *   - 2 open ports (straight / corner / bend): 1 route (trivial).
 *   - 3 open ports (T-junction, Y-junction, wye …): 3 routes — one
 *     per pair of open ports. Each state activates a different
 *     "through" line and parks the third port onto it.
 *   - 4 open ports (cross): 3 PERFECT MATCHINGS — every port routed
 *     somewhere, with the three possible disjoint pair groupings
 *     (e.g. {N-S, E-W}, {N-E, S-W}, {N-W, S-E}).
 *   - 5+ open ports: every single-pair routing (others route to the
 *     closer endpoint), useful as a flexible fallback.
 *
 * Each returned route is a `Map<entryDirIdx, exitDirIdx>` keyed by
 * the port the train ENTERED through (matching how the train's
 * update loop computes `entryDir = OPPOSITE(exitDir)`).
 *
 * Returns `null` only when the junction has fewer than 2 open ports
 * — nothing meaningful to route. A 2-port "junction" still gets a
 * single trivial route so the switch UI can show "1/1" state.
 *
 * BACK-COMPAT: legacy callers reading `routeA` / `routeB` get the
 * first two routes from the new list (or both pointing at the same
 * route when only one exists).
 */
export function deriveSwitchRoutesFromConnections(conn) {
  const opens = [];
  for (let i = 0; i < 8; i++) if (conn[i]) opens.push(i);
  if (opens.length < 2) return null;

  // Shortest arc (in 45° steps) between two direction indices.
  // 0 == same direction, 4 == opposite. Used to pick which end of
  // the main pair an "extra" port should hook into — the closer
  // endpoint, so trains take the gentler curve.
  const arc = (a, b) => {
    const d = Math.abs(a - b) % 8;
    return Math.min(d, 8 - d);
  };

  /** Build a single route Map given a main connected pair (a, b)
   *  and an array of "extra" ports that need to be routed to one
   *  of the main pair members. */
  const buildRoute = (mainA, mainB, extras) => {
    const r = new Map();
    r.set(mainA, mainB);
    r.set(mainB, mainA);
    for (const e of extras) {
      const da = arc(e, mainA);
      const db = arc(e, mainB);
      r.set(e, da <= db ? mainA : mainB);
    }
    return r;
  };

  const routes = [];

  if (opens.length === 2) {
    // Trivial pass-through — only one possible routing.
    routes.push(buildRoute(opens[0], opens[1], []));
  } else {
    // 3+ opens (T / Y / cross / 5-way / …): enumerate every
    // unordered (a, b) pair as the main connected line, route
    // any leftover ports onto the closer endpoint. This gives
    // C(n, 2) states — exactly one per "active direction" the
    // player can pick. A 4-port cross now cycles through ALL
    // SIX pair rotations (NE, N-S, NW, E-W, SE, SW) instead of
    // just the three N-anchored "perfect matchings", so the SE
    // and SW corner arcs are reachable. A 3-port T-junction
    // yields its natural 3 states; a 5-way star yields 10; etc.
    for (let i = 0; i < opens.length; i++) {
      for (let j = i + 1; j < opens.length; j++) {
        const a = opens[i];
        const b = opens[j];
        const extras = opens.filter((p) => p !== a && p !== b);
        routes.push(buildRoute(a, b, extras));
      }
    }
  }

  // Back-compat fields so older callers that still read
  // `routeA` / `routeB` keep working until they migrate.
  return {
    routes,
    routeA: routes[0] || null,
    routeB: routes[1] || routes[0] || null,
  };
}

/**
 * Derive a switch's two states (A and B) directly from the UNDERLYING
 * track tile's connection mask, so a player can just drop a switch on
 * any junction (T, cross, Y/wye family, half-diagonal bend …) and it
 * "just works" — no need to pick the right `baseType` + `rot` combo.
 *
 * Strategy:
 *   - opens = the indices i in 0..7 where conn[i] is true (one per
 *     open port, cardinal + diagonal mixed).
 *   - 2 opens (straight line / corner / bend): trivial — no real
 *     switching possible, both states equal the open pair.
 *   - 3 opens (T-junction, Y-junction, wye, …): find the pair that
 *     is closest to a perfect 180° "through" line — that's state A
 *     (the main run). The remaining port is the branch. State B
 *     swaps in the branch in place of the through-pair member that
 *     produces the gentler curve (smaller angular kink), so a train
 *     coming off the main line takes the most natural diverging path.
 *   - 4 opens (cross-style): state A = the first "perfectly opposite"
 *     pair (e.g. N-S), state B = the other (e.g. E-W).
 *   - 5+ opens: state A = the straightest pair; state B = the second
 *     straightest disjoint pair. The remaining ports stay closed in
 *     each state, so the switch always exposes exactly one through.
 *
 * Returns 8-element boolean arrays in the same layout as TILE_DEFS.
 */
export function deriveSwitchStatesFromConnections(conn) {
  const opens = [];
  for (let i = 0; i < 8; i++) if (conn[i]) opens.push(i);

  const empty = () => [false, false, false, false, false, false, false, false];
  const setPair = (a, b) => {
    const out = empty();
    out[a] = true;
    out[b] = true;
    return out;
  };
  // Angular "kink" between two direction indices (0..7 each one
  // representing a 45° wedge). 0 == perfectly opposite / a straight
  // line; 4 == same direction. Lower == straighter.
  const kink = (a, b) => Math.abs(((a - b + 8) % 8) - 4);

  if (opens.length < 2) {
    return { stateA: empty(), stateB: empty() };
  }
  if (opens.length === 2) {
    const pair = setPair(opens[0], opens[1]);
    return { stateA: pair, stateB: pair };
  }
  if (opens.length === 3) {
    // Pick the pair with the smallest kink as the main through.
    let bestPair = [opens[0], opens[1]];
    let bestK = kink(opens[0], opens[1]);
    for (let i = 0; i < opens.length; i++) {
      for (let j = i + 1; j < opens.length; j++) {
        const k = kink(opens[i], opens[j]);
        if (k < bestK) {
          bestK = k;
          bestPair = [opens[i], opens[j]];
        }
      }
    }
    const [a, b] = bestPair;
    const branch = opens.find((p) => p !== a && p !== b);
    // For state B keep the through-port that produces the gentler
    // curve to the branch (so trains on the main line bend less
    // when diverted).
    const ka = kink(a, branch);
    const kb = kink(b, branch);
    const keep = ka <= kb ? a : b;
    return {
      stateA: setPair(a, b),
      stateB: setPair(keep, branch),
    };
  }
  // 4+ opens: find two disjoint pairs, prefer perfectly-opposite
  // (kink 0) pairs so a cross becomes "horizontal vs vertical".
  const allPairs = [];
  for (let i = 0; i < opens.length; i++) {
    for (let j = i + 1; j < opens.length; j++) {
      allPairs.push({ a: opens[i], b: opens[j], k: kink(opens[i], opens[j]) });
    }
  }
  allPairs.sort((p, q) => p.k - q.k);
  const first = allPairs[0];
  const second = allPairs.find(
    (p) =>
      p.a !== first.a && p.a !== first.b && p.b !== first.a && p.b !== first.b,
  );
  if (!second) {
    // Degenerate (shouldn't happen for valid tiles): reuse the
    // first pair for both states.
    const pair = setPair(first.a, first.b);
    return { stateA: pair, stateB: pair };
  }
  return {
    stateA: setPair(first.a, first.b),
    stateB: setPair(second.a, second.b),
  };
}
